'use client'

import { useState, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Eye, EyeOff, KeyRound, Loader2 } from 'lucide-react'
import { CrmProviderLogo } from './crm-provider-logo'
import { normalizeSubdomain } from '@/lib/crm/normalize-subdomain'
import type { CrmProviderInfo, CrmIntegrationRecord, CrmIntegrationApiError } from '@/hooks/integrations/use-crm-integrations'

interface UpdateCredentialsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  integration: CrmIntegrationRecord | null
  provider: CrmProviderInfo | undefined
  onUpdate: (
    integrationId: string,
    credentials: Record<string, unknown>,
    metadata?: Record<string, unknown>,
  ) => Promise<{ success: boolean; error?: string }>
  isUpdating: boolean
  error: CrmIntegrationApiError | null
  onClearError: () => void
}

export function UpdateCredentialsDialog({
  open,
  onOpenChange,
  integration,
  provider,
  onUpdate,
  isUpdating,
  error,
  onClearError,
}: UpdateCredentialsDialogProps) {
  const [showPassword, setShowPassword] = useState<Record<string, boolean>>({})
  const [formData, setFormData] = useState<Record<string, string>>({})

  const resetDialog = useCallback(() => {
    setShowPassword({})
    setFormData({})
    onClearError()
  }, [onClearError])

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        resetDialog()
      }
      onOpenChange(newOpen)
    },
    [onOpenChange, resetDialog],
  )

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!integration || !provider) return

      const credentials: Record<string, unknown> = {}
      const metadata: Record<string, unknown> = {}

      // Re-pasting a key only applies to the long-token method (OAuth integrations reconnect via
      // the OAuth flow, not by editing credentials here).
      const longTokenFields = provider.authMethods.find((m) => m.method === 'long_token')?.fields ?? []

      // Only include credential fields (skip 'name' field)
      longTokenFields.forEach((field) => {
        if (field.name === 'name') return
        if (field.name === 'subdomain') {
          const subdomain = normalizeSubdomain(formData[field.name] || '')
          credentials[field.name] = subdomain
          metadata[field.name] = subdomain
        } else {
          credentials[field.name] = formData[field.name]
        }
      })

      const hasMetadata = Object.keys(metadata).length > 0
      const result = await onUpdate(integration.id, credentials, hasMetadata ? metadata : undefined)
      if (result.success) {
        handleOpenChange(false)
      }
    },
    [integration, provider, formData, onUpdate, handleOpenChange],
  )

  const togglePasswordVisibility = useCallback((fieldName: string) => {
    setShowPassword((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }))
  }, [])

  // Only validate credential fields (not 'name'); sourced from the long-token method.
  const credentialFields =
    provider?.authMethods.find((m) => m.method === 'long_token')?.fields.filter((f) => f.name !== 'name') ?? []
  const isFormValid = credentialFields.every(
    (field) => !field.required || (formData[field.name] && formData[field.name].trim().length > 0),
  )

  if (!integration || !provider) return null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CrmProviderLogo provider={integration.provider} logoUrl={provider.logoUrl} size={20} />
            <span>Update Credentials</span>
          </DialogTitle>
          <DialogDescription>
            <span>Enter new API credentials for</span> {integration.name}<span>. The old credentials will be replaced.</span>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error.error}</AlertDescription>
            </Alert>
          )}

          {credentialFields.map((field) => (
            <div key={field.name}>
              <Label htmlFor={`update-${field.name}`}>
                {field.label} <span>{field.required && '*'}</span>
              </Label>
              <div className="relative mt-1">
                <Input
                  id={`update-${field.name}`}
                  name={`update-nofill-${field.name}`}
                  type={field.type === 'password' && !showPassword[field.name] ? 'password' : 'text'}
                  value={formData[field.name] || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      [field.name]:
                        field.name === 'subdomain' ? normalizeSubdomain(e.target.value) : e.target.value,
                    })
                  }
                  placeholder={field.placeholder}
                  required={field.required}
                  autoComplete="new-password"
                />
                {field.type === 'password' && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => togglePasswordVisibility(field.name)}
                  >
                    {showPassword[field.name] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                )}
              </div>
              {field.helpText && (
                <p className="text-xs text-gray-500 mt-1">{field.helpText}</p>
              )}
            </div>
          ))}

          <div className="bg-green-50 border border-green-200 rounded p-3">
            <p className="text-xs text-green-800">
              <strong>Secure:</strong> <span>Your credentials will be encrypted and stored securely.</span>
            </p>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button
              type="submit"
              disabled={isUpdating || !isFormValid}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isUpdating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  <span key="updating">Updating...</span>
                </>
              ) : (
                <>
                  <KeyRound className="w-4 h-4 mr-2" />
                  <span key="update-credentials">Update Credentials</span>
                </>
              )}
            </Button>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
